// apps/client/src/world/objects/board.ts — the plaza notice board (assets/builders/board.ts).
// M1-T17: a soft warm glow on the board face while there is news you haven't seen (newsUnread(mailNewsHead, mailNewsSeen)): an
// emissive pulse 0 → 0.35 over 2 s, driven from the face mesh's onBeforeRender, which reads the store each frame. The face gets its own
// material copy (the builder's toy materials are shared by colour), and the view stays out of the static batch so the pulse renders.
// Everything else (posts, header, notes, roof) is baked into one mesh: two draws instead of eight (spec §8.7, M2 playtest).
import * as THREE from 'three';
import { bakeMeshes } from '../bake';
import { newsUnread } from '../../state/slices/mail';
import { useGame } from '../../state/store';
import type { ObjectView } from '../objectView';

const GLOW = '#F6C85F', MAX = 0.35, PERIOD_MS = 2000;
const box = new THREE.Box3(), size = new THREE.Vector3();

/** The mesh with the largest upright (x × y) extent: the board face, not the posts, notes, roof or the flat contact blob. */
export function boardFace(root: THREE.Object3D): THREE.Mesh | null {
  root.updateWorldMatrix(true, true);
  let best: { m: THREE.Mesh; area: number } | null = null;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    box.setFromObject(m).getSize(size);
    const area = size.x * size.y;
    if (!best || area > best.area) best = { m, area };
  });
  return (best as { m: THREE.Mesh } | null)?.m ?? null;
}
/** Emissive intensity at time `ms` while news is unread: a smooth 0 → MAX → 0 pulse. */
export const glowAt = (ms: number): number => MAX * (0.5 - 0.5 * Math.cos((2 * Math.PI * ms) / PERIOD_MS));

export const view: ObjectView = {
  kinds: ['board'],
  async build(obj, ctx) {
    const o = await ctx.assets.model(obj.catalogId);
    const face = boardFace(o);
    const src = face && !Array.isArray(face.material) ? face.material : null;
    if (face && src instanceof THREE.MeshStandardMaterial) {
      const mat = src.clone();
      mat.emissive = new THREE.Color(GLOW);
      mat.emissiveIntensity = 0;
      face.material = mat;
      face.userData.own = true;                                   // the builder made this face for this board alone: its owner frees it
      face.onBeforeRender = () => {
        const s = useGame.getState();
        mat.emissiveIntensity = newsUnread(s.mailNewsHead, s.mailNewsSeen) ? glowAt(performance.now()) : 0;
      };
    }
    const rest = bakeMeshes(o, { skip: (m) => m === face, castShadow: true }), old: THREE.Object3D[] = [];
    o.traverse((c) => { if ((c as THREE.Mesh).isMesh && c !== face && !c.userData.blob) old.push(c); });
    for (const c of old) c.removeFromParent();
    o.add(...rest);
    return o;
  },
};
